import { useEffect, useRef, useCallback } from 'react';

type BarcodeScannerDebugInfo = {
  buffer: string;
  avgMs: number;
  accepted: boolean;
  reason?: string;
};

type UseBarcodeScannerOptions = {
  onScan: (value: string) => void;
  enabled?: boolean;
  minLength?: number;
  maxLength?: number;
  scanTimeoutMs?: number;
  avgCharThresholdMs?: number;
  preventDefaultOnScan?: boolean;
  /**
   * Не срабатывать, когда фокус в редактируемом элементе (input/textarea/select/
   * contentEditable). По умолчанию true: иначе глобальный capture-слушатель съедает
   * быстрый ввод и paste чужих инпутов (весы, вставляющие значение в поле веса).
   * Отключать только если экран должен ловить сканы поверх сфокусированных полей.
   */
  ignoreFocusedInputs?: boolean;
  duplicateScanWindowMs?: number;
  /** Ожидаемый префикс от сканера (например '\x02' STX). Если задан — скан без префикса игнорируется. */
  prefix?: string;
  /** Завершающий символ скана. По умолчанию 'Enter'. Можно задать '\x03' (ETX) и т.п. */
  suffix?: string;
  onDebug?: (info: BarcodeScannerDebugInfo) => void;
};

type BarcodeScannerState = {
  lastScannedValue: string | null;
  resetLastScan: () => void;
};

const DEFAULTS = {
  enabled: true,
  minLength: 3,
  maxLength: 100,
  scanTimeoutMs: 200,
  avgCharThresholdMs: 100,
  preventDefaultOnScan: true,
  ignoreFocusedInputs: true,
  duplicateScanWindowMs: 200,
} as const;

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;

  const tagName = target.tagName.toLowerCase();

  return (
    target.isContentEditable ||
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select'
  );
};

const isAllowedKey = (event: KeyboardEvent): boolean => {
  if (event.ctrlKey || event.altKey || event.metaKey) return false;
  return event.key.length === 1;
};

export const useBarcodeScanner = (
  options: UseBarcodeScannerOptions
): BarcodeScannerState => {
  const {
    onScan,
    enabled = DEFAULTS.enabled,
    minLength = DEFAULTS.minLength,
    maxLength = DEFAULTS.maxLength,
    scanTimeoutMs = DEFAULTS.scanTimeoutMs,
    avgCharThresholdMs = DEFAULTS.avgCharThresholdMs,
    preventDefaultOnScan = DEFAULTS.preventDefaultOnScan,
    ignoreFocusedInputs = DEFAULTS.ignoreFocusedInputs,
    duplicateScanWindowMs = DEFAULTS.duplicateScanWindowMs,
    prefix,
    suffix = 'Enter',
    onDebug,
  } = options;

  const bufferRef = useRef<string>('');
  const timestampsRef = useRef<number[]>([]);
  const timeoutRef = useRef<number | null>(null);

  const onScanRef = useRef(onScan);
  const onDebugRef = useRef(onDebug);

  const lastScanValueRef = useRef<string | null>(null);
  const lastScanTimeRef = useRef<number>(0);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    onDebugRef.current = onDebug;
  }, [onDebug]);

  const resetLastScan = useCallback(() => {
    lastScanValueRef.current = null;
    lastScanTimeRef.current = 0;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const reset = () => {
      bufferRef.current = '';
      timestampsRef.current = [];

      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const scheduleReset = () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(reset, scanTimeoutMs);
    };

    const calcAvgInterval = (): number => {
      const timestamps = timestampsRef.current;
      if (timestamps.length < 2) return 0;

      let totalDiff = 0;
      for (let i = 1; i < timestamps.length; i += 1) {
        totalDiff += timestamps[i] - timestamps[i - 1];
      }

      return totalDiff / (timestamps.length - 1);
    };

    const isScannerInput = (): boolean => {
      if (bufferRef.current.length < minLength) return false;
      if (timestampsRef.current.length < 2) return true;

      return calcAvgInterval() <= avgCharThresholdMs;
    };

    const isDuplicateScan = (value: string, now: number): boolean => {
      if (!lastScanValueRef.current) return false;

      return (
        lastScanValueRef.current === value &&
        now - lastScanTimeRef.current <= duplicateScanWindowMs
      );
    };

    const emitDebug = (buffer: string, avgMs: number, accepted: boolean, reason?: string) => {
      onDebugRef.current?.({ buffer, avgMs, accepted, reason });
    };

    const commitScan = (value: string, now: number) => {
      lastScanValueRef.current = value;
      lastScanTimeRef.current = now;
      onScanRef.current(value);
    };

    const isSuffixKey = (event: KeyboardEvent): boolean => {
      return event.key === suffix;
    };

    const stripPrefix = (value: string): string | null => {
      if (!prefix) return value;
      if (!value.startsWith(prefix)) return null;
      return value.slice(prefix.length);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (ignoreFocusedInputs && isEditableTarget(event.target)) return;

      const now = performance.now();

      if (isSuffixKey(event)) {
        const raw = bufferRef.current.trim();
        const avgMs = calcAvgInterval();
        const stripped = stripPrefix(raw);

        if (!stripped || !isScannerInput()) {
          emitDebug(raw, avgMs, false, stripped === null ? 'prefix_mismatch' : 'not_scanner_input');
          reset();
          return;
        }

        if (preventDefaultOnScan) {
          event.preventDefault();
          event.stopPropagation();
        }

        if (isDuplicateScan(stripped, now)) {
          emitDebug(stripped, avgMs, false, 'duplicate');
          reset();
          return;
        }

        emitDebug(stripped, avgMs, true);
        commitScan(stripped, now);
        reset();
        return;
      }

      if (!isAllowedKey(event)) return;

      bufferRef.current += event.key;

      if (bufferRef.current.length > maxLength) {
        reset();
        return;
      }

      timestampsRef.current.push(now);

      if (preventDefaultOnScan && bufferRef.current.length >= 2 && isScannerInput()) {
        event.preventDefault();
      }

      scheduleReset();
    };

    const handlePaste = (event: ClipboardEvent) => {
      if (ignoreFocusedInputs && isEditableTarget(event.target)) return;

      const raw = event.clipboardData?.getData('text')?.trim();
      if (!raw) return;

      const stripped = stripPrefix(raw);
      if (!stripped || stripped.length < minLength || stripped.length > maxLength) return;

      event.preventDefault();
      const now = performance.now();

      if (isDuplicateScan(stripped, now)) {
        emitDebug(stripped, 0, false, 'duplicate_paste');
        return;
      }

      emitDebug(stripped, 0, true);
      commitScan(stripped, now);
    };

    const pasteListener = handlePaste as EventListener;

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('paste', pasteListener, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('paste', pasteListener, true);
      reset();
    };
  }, [
    enabled,
    minLength,
    maxLength,
    scanTimeoutMs,
    avgCharThresholdMs,
    preventDefaultOnScan,
    ignoreFocusedInputs,
    duplicateScanWindowMs,
    prefix,
    suffix,
  ]);

  return {
    lastScannedValue: lastScanValueRef.current,
    resetLastScan,
  };
};
